import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Calendar,
  CheckCircle,
  Clock,
  Download,
  ExternalLink,
  Info,
} from "lucide-react";
import type { Invoice } from "@/app/billing/types";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { getStatusConfig } from "./utils";

export const createInvoiceColumns = (
  formatDate: (date?: string | null) => string,
  formatCurrency: (amount: string, currency?: string) => string
): ColumnDef<Invoice>[] => [
  {
    id: "invoice",
    accessorKey: "invoice_number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Invoice" />
    ),
    cell: ({ row }) => {
      const statusConfig = getStatusConfig(row.original.status);
      const StatusIcon = statusConfig.icon;

      return (
        <div className="flex min-w-[180px] items-center gap-3">
          <StatusIcon className={`h-4 w-4 ${statusConfig.color}`} />
          <div>
            <div className="font-medium text-sm">
              {row.original.invoice_number}
            </div>
            <Badge
              className={`mt-1 font-medium text-xs ${statusConfig.className}`}
              variant={statusConfig.variant}
            >
              {row.original.status.charAt(0).toUpperCase() +
                row.original.status.slice(1)}
            </Badge>
          </div>
        </div>
      );
    },
  },
  {
    id: "dates",
    accessorKey: "invoice_date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Dates" />
    ),
    cell: ({ row }) => (
      <div className="min-w-[200px] space-y-1">
        <div className="flex items-center gap-1 text-sm">
          <Calendar className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground text-xs">Issued:</span>
          <span className="text-xs">
            {formatDate(row.original.invoice_date)}
          </span>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Clock className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground text-xs">Due:</span>
          <span className="text-xs">{formatDate(row.original.due_date)}</span>
        </div>
        {!!row.original.paid_at && (
          <div className="flex items-center gap-1 text-green-600 text-sm">
            <CheckCircle className="h-3 w-3 flex-shrink-0" />
            <span className="text-xs">
              Paid: {formatDate(row.original.paid_at)}
            </span>
          </div>
        )}
      </div>
    ),
  },
  {
    id: "amount",
    accessorKey: "total",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => (
      <div>
        <span className="font-semibold text-sm">
          {formatCurrency(row.original.total, row.original.currency)}
        </span>
        {row.original.amount_due !== "0.00" && (
          <div className="text-muted-foreground text-xs">
            Due:{" "}
            {formatCurrency(row.original.amount_due, row.original.currency)}
          </div>
        )}
      </div>
    ),
  },
  {
    id: "billed_services",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Services" />
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span className="font-bold">{row.original.line_items.length}</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  aria-label="Show billed services details"
                  className="flex items-center rounded-full p-1 transition hover:bg-muted focus:bg-muted focus:outline-none"
                  style={{ lineHeight: 0 }}
                  type="button"
                />
              }
            >
              <Info className="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm p-4" side="top">
              <div className="mb-2 font-semibold text-sm">
                Billed Services Details
              </div>
              {row.original.line_items.length > 0 ? (
                <div className="max-h-60 space-y-2 overflow-y-auto">
                  {row.original.line_items.map((item) => (
                    <div
                      className="flex items-center justify-between gap-8 border-b py-1 last:border-b-0"
                      key={item.id}
                    >
                      <span className="min-w-[140px]">{item.name}</span>
                      <span className="font-mono">
                        {formatCurrency(item.amount, row.original.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground text-xs">
                  No billed services
                </span>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <div className="flex min-w-[120px] items-center justify-end gap-2">
        <Button
          render={
            // biome-ignore lint/a11y/useAnchorContent: children rendered by Base UI render prop
            <a
              className="flex items-center gap-2"
              href={row.original.hosted_invoice_url}
              rel="noopener noreferrer"
              target="_blank"
            />
          }
          size="sm"
          variant="secondary"
        >
          <ExternalLink className="h-3 w-3" />
          View
        </Button>
        {!!row.original.invoice_pdf && (
          <Button
            render={
              // biome-ignore lint/a11y/useAnchorContent: children rendered by Base UI render prop
              <a
                className="flex items-center gap-2"
                href={row.original.invoice_pdf}
                rel="noopener noreferrer"
                target="_blank"
              />
            }
            size="sm"
            variant="outline"
          >
            <Download className="h-3 w-3" />
            PDF
          </Button>
        )}
      </div>
    ),
  },
];
